/* eslint-disable jsx-a11y/media-has-caption */
import { Box, Stack } from '@mui/material'
import { ContentTitle, SectionTitle } from '@/src/components/title'
import { Switch, Typography } from '@evocolabs/orkaui'
import { useEffect, useRef, useState } from 'react'

import CDN from '@/src/utils/CDN'
import Image from 'next/image'
import Start from '../../Player/Start'
import Stop from '../../Player/Stop'
import joinCDN from '@/src/utils/CDN'
import styles from './styles.module.scss'

const EXPLORE_ORKA_TWO_PATH = '/website/explore-orka-two'
const data = {
  title: `Cutting-Edge Technology for Better Hearing`,
  video: {
    poster: `${EXPLORE_ORKA_TWO_PATH}/PC/denoise_poster.png`,
    source: [
      {
        key: 'PC',
        src: `${EXPLORE_ORKA_TWO_PATH}/PC/denoise.mp4`
      }
    ]
  },
  steps: [
    {
      denoise: 'AI DeNoise',
      reduction: 'Automatic Noise Reduction',
      listen: 'Listen to Demo',
      disable: 'Off',
      enable: 'On',
      footer:
        // eslint-disable-next-line max-len
        'Orka Two adjusts and amplifies human voices, filters out noises, and balances speech clarity and noise reduction. ',
      audio1: '/website/product/orkaTwo/sample_3.wav',
      audio2: '/website/product/orkaTwo/denoise_sample_3.wav'
    },
    {
      title: 'Environmental Classifier',
      title2: 'Clarity Everywhere You Go',
      subTitle:
        // eslint-disable-next-line max-len
        'No matter where you are, Orka Two captures every detail, ensuring no conversation is missed.'
    },
    {
      title: 'Adaptive Sound Directionality',
      title2: ['"Hear Everyone" Mode', '"Speech Focus" Mode'],
      subTitle: [
        // eslint-disable-next-line max-len
        'Engage in any group conversation and hear everyone in the entire room.',
        'Focus on the one-on-one conversation with the person in front of you.'
      ],
      people: '/website/product/orkaTwo/people.jpg',
      people2: '/website/product/orkaTwo/people_2.jpg',
      people3: '/website/product/orkaTwo/people_3.jpg',
      button: [
        '/website/product/orkaTwo/hear_everyone_1.svg',
        '/website/product/orkaTwo/speech_focus_1.svg'
      ]
    }
  ]
}

const AIDeNoise = (value: (typeof data.steps)[0]) => {
  const [isPlay, setIsPlay] = useState(false)
  const [isEnabled, setIsEnabled] = useState(false)
  const audioRef = useRef<HTMLVideoElement>(null)
  const denoiseAudioRef = useRef<HTMLVideoElement>(null)
  const { enable, disable } = value

  const Icon = isPlay ? Stop : Start
  const text = isEnabled ? enable : disable

  const togglePlay = () => {
    setIsPlay((prev) => !prev)
  }

  const toggleDeNoise = () => {
    setIsEnabled((prev) => !prev)
  }

  // update audio status
  useEffect(() => {
    if (!audioRef || !denoiseAudioRef) return

    if (!isPlay) {
      audioRef.current.pause()
      denoiseAudioRef.current.pause()
      return
    }

    if (isEnabled) {
      audioRef.current.pause()
      denoiseAudioRef.current.play()
    } else {
      denoiseAudioRef.current.pause()
      audioRef.current.play()
    }
  }, [isPlay, isEnabled])

  return (
    <Stack flexDirection={{ xs: 'column', md: 'row' }} gap={{ xs: 3, md: 8 }}>
      <Box flex="1" flexBasis="50%" className={styles['deNoise-video-wrapper']}>
        <video
          poster={CDN(data.video.poster)}
          autoPlay
          muted
          loop
          playsInline
          className={styles['deNoise-video']}
        >
          {data.video.source.map(({ key, src }) => (
            <source key={key} src={CDN(src)} type="video/mp4" />
          ))}
        </video>
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        flex="1"
        justifyContent="center"
        flexBasis="50%"
        pr={{ xs: 'auto', md: 'max(108px, (100vw - 1440px) / 2)' }}
      >
        <ContentTitle textAlign={{ xs: 'center', md: 'left' }}>
          {value.denoise}
        </ContentTitle>
        <Typography
          variant="t2"
          color="#30445E"
          textAlign={{ xs: 'center', md: 'left' }}
        >
          {value.reduction}
        </Typography>
        <Box display="none">
          <audio
            src={CDN(value.audio1)}
            loop
            muted={false}
            preload="auto"
            id="explore-orkatwo-technology-audio1"
            ref={audioRef}
          />
          <audio
            src={CDN(value.audio2)}
            loop
            muted={false}
            preload="auto"
            id="explore-orkatwo-technology-audio2"
            ref={denoiseAudioRef}
          />
        </Box>
        <Box sx={{ 'userSelect': 'none' }}>
          {isPlay ? (
            <Box
              display="flex"
              justifyContent={{ xs: 'center', md: 'flex-start' }}
              alignItems="center"
              flexWrap={{ xs: 'wrap', md: 'nowrap' }}
              my="24px"
              sx={{
                '&>.MuiFormControlLabel-root': {
                  ml: '2px'
                }
              }}
            >
              <Box
                onClick={togglePlay}
                display={{ xs: 'block', md: 'inline' }}
                textAlign="center"
                sx={{ cursor: 'pointer' }}
                flexBasis={{ xs: '100%', md: 'auto' }}
                id="explore-orka-two-listen-demo-button-stop"
              >
                <Icon />
              </Box>
              <Box width="136px" ml={{ xs: '0', md: '16px' }}>
                <Typography variant="Body2" component="span" color="#30445E">
                  {value.denoise}
                </Typography>
                <Typography
                  variant="Body2"
                  component="span"
                  color="#30445E"
                  ml="4px"
                >
                  {text}
                </Typography>
              </Box>
              <Switch
                checked={isEnabled}
                onChange={toggleDeNoise}
                id="explore-orka-two-switch-denoise"
              />
            </Box>
          ) : (
            <Box
              display="flex"
              flexDirection={{ xs: 'column', md: 'row' }}
              alignItems="center"
              my="24px"
            >
              <Box
                onClick={togglePlay}
                sx={{ cursor: 'pointer' }}
                display={{ xs: 'block', md: 'inline' }}
                mr={{ xs: 0, md: 2 }}
                id="explore-orka-two-listen-demo-button-play"
              >
                <Icon />
              </Box>
              <Typography variant="T1" component="span" color="#30445E">
                {value.listen}
              </Typography>
            </Box>
          )}
          <Typography variant="body1" textAlign={{ xs: 'center', md: 'left' }}>
            {value.footer}
          </Typography>
        </Box>
      </Box>
    </Stack>
  )
}

const Environmental = (value: (typeof data.steps)[1]) => {
  return (
    <Box
      display="flex"
      flexDirection={{ xs: 'column', md: 'row' }}
      mx={{ xs: '0 16px', md: 'max(108px, (100vw - 1440px) / 2)' }}
    >
      <Box
        display="flex"
        flexDirection="column"
        flex="1"
        pr={{ xs: 0, md: '24px' }}
        mr={{ xs: 0, md: '40px' }}
        mt={{ xs: '24px', md: 0 }}
        justifyContent="center"
        order={1}
      >
        <Typography
          variant="T1"
          color="#30445E"
          textAlign={{ xs: 'center', md: 'left' }}
        >
          {value.title}
        </Typography>
        <Typography
          variant="t1"
          color="#30445E"
          textAlign={{ xs: 'center', md: 'left' }}
        >
          {value.title2}
        </Typography>
        <Typography
          variant="body1"
          mt="16px"
          textAlign={{ xs: 'center', md: 'left' }}
        >
          {value.subTitle}
        </Typography>
      </Box>
      <Box
        flex="1"
        order={{ xs: 0, md: 1 }}
        display="flex"
        justifyContent="center"
      >
        <video
          // eslint-disable-next-line max-len
          poster={joinCDN('/website/buy-orka-two/V2/PC/environmental-classifier.jpg', 'US')}
          autoPlay
          loop
          muted
          style={{ width: '100%', borderRadius: '24px' }}
          src={
            // eslint-disable-next-line max-len
            joinCDN('/website/buy-orka-two/V2/PC/environmental-classifier.mp4')
          }
          playsInline
        />
      </Box>
    </Box>
  )
}

const Adaptive = (value: (typeof data.steps)[2]) => {
  const [selected, setSelected] = useState(true)
  const [intervalId, setIntervalId] = useState<NodeJS.Timeout | null>(null)

  const cleanUpTimer = () => {
    if (!intervalId) return
    clearInterval(intervalId)
    setIntervalId(null)
  }
  useEffect(() => {
    const id = setInterval(() => {
      setSelected((prev) => !prev)
    }, 5000)
    setIntervalId(id)

    return cleanUpTimer
  }, [])

  const selectSpeechFocus = () => {
    setSelected(false)
    cleanUpTimer()
  }
  const selectHearEveryone = () => {
    setSelected(true)
    cleanUpTimer()
  }

  return (
    <Box
      display="flex"
      flexDirection={{ xs: 'column', md: 'row' }}
      mx={{ xs: '0 16px', md: 'max(108px, (100vw - 1440px) / 2)' }}
    >
      <Box display="flex" flexDirection="row" flex="1">
        <Box
          display={{ xs: 'none', md: 'flex' }}
          flexDirection="column"
          alignItems="center"
          flex="1"
        >
          <Image
            src={CDN(value.people)}
            width={267}
            height={303}
            style={{
              cursor: 'pointer',
              filter: selected ? 'none' : 'grayscale(1)',
              boxShadow: selected ? '0 0 0 6px #cc9a39' : 'none',
              borderRadius: '24px'
            }}
            onClick={selectHearEveryone}
            alt={value.title + ' ' + value.subTitle}
          />
          <Box
            className={`${styles['mode-toggle-button']} ${
              selected ? '' : styles['mode-toggle-button__disabled']
            }`}
            onClick={selectHearEveryone}
          >
            <Box className={styles.overlay} />
            <Image
              width={120}
              height={62}
              src={CDN(value.button[0])}
              alt={value.title + ' ' + value.subTitle}
            />
          </Box>
        </Box>

        <Box
          display={{ xs: 'none', md: 'flex' }}
          flexDirection="column"
          flex="1"
          ml="16px"
          alignItems="center"
        >
          <Image
            width={267}
            height={303}
            src={selected ? CDN(value.people2) : CDN(value.people3)}
            style={{
              cursor: 'pointer',
              filter: !selected ? 'none' : 'grayscale(1)',
              boxShadow: !selected ? '0 0 0 6px #cc9a39' : 'none',
              borderRadius: '24px'
            }}
            onClick={selectSpeechFocus}
            alt={value.title + ' ' + value.subTitle}
          />
          <Box
            className={`${styles['mode-toggle-button']} ${
              !selected ? '' : styles['mode-toggle-button__disabled']
            }`}
            onClick={selectSpeechFocus}
          >
            <Box className={styles.overlay}></Box>
            <Image
              width={120}
              height={62}
              src={CDN(value.button[1])}
              alt={value.title + ' ' + value.subTitle}
            />
          </Box>
        </Box>

        <Box
          display={{ xs: 'flex', md: 'none' }}
          flexWrap="wrap"
          flexDirection="column"
          width="100%"
          alignItems="center"
        >
          <Image
            width={267}
            height={303}
            src={!selected ? CDN(value.people3) : CDN(value.people)}
            alt={value.title + ' ' + value.subTitle}
          />
          <Box display="flex" width={267} justifyContent="center" mb={8}>
            <Box
              className={`${styles['mode-toggle-button']} ${
                selected ? '' : styles['mode-toggle-button__disabled']
              }`}
              onClick={selectHearEveryone}
            >
              <Box className={styles.overlay} />
              <Image
                width={120}
                height={62}
                src={CDN(value.button[0])}
                alt={value.title + ' ' + value.subTitle}
              />
            </Box>
            <Box
              className={`${styles['mode-toggle-button']} ${
                !selected ? '' : styles['mode-toggle-button__disabled']
              }`}
              onClick={selectSpeechFocus}
            >
              <Box className={styles.overlay} />
              <Image
                width={120}
                height={62}
                src={CDN(value.button[1])}
                alt={value.title + ' ' + value.subTitle}
              />
            </Box>
          </Box>
        </Box>
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        flex="1"
        pr={{ xs: 0, md: '24px' }}
        ml={{ xs: 0, md: '40px' }}
        justifyContent="center"
      >
        <Typography
          variant="T1"
          color="#30445E"
          textAlign={{ xs: 'center', md: 'left' }}
        >
          {value.title}
        </Typography>
        <Typography
          variant="t1"
          color="#30445E"
          textAlign={{ xs: 'center', md: 'left' }}
        >
          {value.title2[selected ? 0 : 1]}
        </Typography>
        <Typography
          variant="body2"
          mt="16px"
          textAlign={{ xs: 'center', md: 'left' }}
        >
          {value.subTitle[selected ? 0 : 1]}
        </Typography>
      </Box>
    </Box>
  )
}

const Technology = () => {
  return (
    <Stack
      width="100%"
      gap={{ xs: 5, dl: 6 }}
      py={{ xs: 5, dl: 10 }}
      px={{ xs: 2, dl: 0 }}
    >
      <SectionTitle fontSize={{ xs: '24px', dl: '40px' }}>
        {data.title}
      </SectionTitle>
      <AIDeNoise {...data.steps[0]} />
      <Environmental {...data.steps[1]} />
      <Adaptive {...data.steps[2]} />
    </Stack>
  )
}

export default Technology
